import { useToast } from 'components/ToastManager';
import {
  UboIdentificationMethod,
  useAuthorizeIndividualLegalEntityBasisTheorySessionMutation,
  useGetIndividualEntityQuery,
  useGetPaymentPlatformEntityQuery,
  useSubmitUboForReviewMutation,
} from 'hooks/internalApi';
import { useContextSafely } from 'hooks/useContextSafely';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import { useWorkspaceId } from 'hooks/useWorkspace';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import { FragmentEnv } from 'utils/env';
import { getResultOrError } from 'utils/errors';
import type { ITextElement, Token } from '@basis-theory/react-elements';
import {
  BasisTheoryProvider,
  useBasisTheory,
} from '@basis-theory/react-elements';
import { useQuery } from '@tanstack/react-query';

import { LegalEntityStatusSchema } from '@fragment/billing/platform/entitySchemas';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { ColorModeContext } from '@fragment/ui/src/components/ColorMode/ColorModeContext';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { Logo } from '@fragment/ui/src/components/Logo/Logo';
import { Toast } from '@fragment/ui/src/components/Toast';

import { NoticeView } from './NoticeView';
import { OnboardingEsignBankDisclosureChecklist } from './PlatformOnboardingSubmitView';
import { BasisTheoryTextElement, FormLoading } from './SharedUI';

const PersonCDDLoadingView = () => (
  <div className="flex flex-col h-fit gap-y-f2 p-f2 max-w-f56">
    <Logo />
    <FormLoading />
  </div>
);

type PersonCDDViewProps = {
  entityId: string;
};

type PersonCDDFormProps = {
  workspaceId: string;
  legalEntityId: string;
  businessEntityId: string;
  token: Token;
  updateToken: (id: string, ref: React.RefObject<ITextElement>) => void;
  isDarkMode: boolean;
  onSubmittedForReview: () => void;
};

const identificationMethodOptions: {
  value: UboIdentificationMethod;
  label: string;
}[] = [
  {
    value: UboIdentificationMethod.SocialSecurityNumber,
    label: 'Social Security Number',
  },
  {
    value: UboIdentificationMethod.IndividualTaxpayerIdentificationNumber,
    label: 'Individual Taxpayer Identification Number',
  },
  { value: UboIdentificationMethod.Passport, label: 'Passport' },
  { value: UboIdentificationMethod.DriversLicense, label: 'Drivers License' },
];

type PersonOnboardingSubmitViewProps = {
  workspaceId: string;
  legalEntityId: string;
  businessEntityId: string;
  identificationMethod: UboIdentificationMethod;
  onSubmitSuccess: () => void;
};

const PersonOnboardingSubmitView = ({
  workspaceId,
  legalEntityId,
  businessEntityId,
  identificationMethod,
  onSubmitSuccess,
}: PersonOnboardingSubmitViewProps) => {
  const { context: internalApiCtx } = useInternalApiContext();
  const { showToast } = useToast();
  const [isCertified, setIsCertified] = useState(false);
  const [isBankDisclosuresChecked, setIsBankDisclosuresChecked] =
    useState(false);

  const [{ fetching: isSubmitting }, submitUbo] =
    useSubmitUboForReviewMutation();

  const canSubmit = isCertified && isBankDisclosuresChecked && !isSubmitting;

  const handleSubmit = useCallback(async () => {
    const result = await submitUbo(
      {
        workspaceId,
        legalEntityId,
        businessEntityId,
        identificationMethod,
      },
      internalApiCtx
    );
    const { isError, errorMessage } = getResultOrError(
      result.data?.submitUBOForReview,
      result.error
    );
    if (isError) {
      showToast(<Toast type="error" message={errorMessage} />);
    } else {
      showToast(
        <Toast type="success" message="Successfully Submitted for Review" />
      );
      onSubmitSuccess();
    }
  }, [
    workspaceId,
    legalEntityId,
    businessEntityId,
    identificationMethod,
    submitUbo,
    internalApiCtx,
    showToast,
    onSubmitSuccess,
  ]);

  return (
    <div className="flex flex-col h-fit gap-y-f2">
      <OnboardingEsignBankDisclosureChecklist
        esignAcknowledged={isCertified}
        onToggleEsignAcknowledged={() => setIsCertified((v) => !v)}
        informationAccurateAcknowledged={isBankDisclosuresChecked}
        onToggleInformationAccurateAcknowledged={() =>
          setIsBankDisclosuresChecked((v) => !v)
        }
      />
      <span>
        Clicking the button below will submit your application for review.
      </span>
      <PrimaryButtonV2 disabled={!canSubmit} onClick={handleSubmit}>
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </PrimaryButtonV2>
    </div>
  );
};

const PersonCDDForm = ({
  workspaceId,
  legalEntityId,
  businessEntityId,
  token,
  updateToken,
  isDarkMode,
  onSubmittedForReview,
}: PersonCDDFormProps) => {
  const [identificationMethod, setIdentificationMethod] =
    useState<UboIdentificationMethod>(
      UboIdentificationMethod.SocialSecurityNumber
    );
  return (
    <div className="flex flex-col h-fit gap-y-f2">
      <span className="text-main">
        The following information is required to complete your onboarding to our
        Payments Platform.
      </span>
      <BasisTheoryTextElement
        id="role"
        label="Company Role / Title"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="firstName"
        label="First Name"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
        type="name"
      />
      <BasisTheoryTextElement
        id="middleName"
        label="Middle Name"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
        type="name"
      />
      <BasisTheoryTextElement
        id="lastName"
        label="Last Name"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
        type="name"
      />
      <BasisTheoryTextElement
        id="email"
        label="Email"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
        type="email"
      />
      <BasisTheoryTextElement
        id="phone"
        label="Phone Number"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
        type="phone"
      />
      <BasisTheoryTextElement
        id="dateOfBirth"
        label="Date of Birth"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
        type="date"
      />
      <BasisTheoryTextElement
        id="addressLine1"
        label="Address Line 1"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="addressLine2"
        label="Address Line 2"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="addressCity"
        label="City"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="addressState"
        label="State"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
        type="state"
      />
      <BasisTheoryTextElement
        id="addressZip"
        label="Zip Code"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <Dropdown
        label="Identification Type"
        options={identificationMethodOptions}
        displayValue={identificationMethod}
        onChange={(value: string) => {
          setIdentificationMethod(value as UboIdentificationMethod);
        }}
        size="md"
        width="w-full"
        height="h-fit"
        padding="p-f0"
        backgroundColor="bg-main-200"
      />
      <BasisTheoryTextElement
        id="identificationNumber"
        label="ID Number"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="countryOfCitizenship"
        label="Country of Citizenship"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <PersonOnboardingSubmitView
        workspaceId={workspaceId}
        legalEntityId={legalEntityId}
        businessEntityId={businessEntityId}
        identificationMethod={identificationMethod}
        onSubmitSuccess={onSubmittedForReview}
      />
    </div>
  );
};

const PersonCDDSubmittedView = () => (
  <div className="flex flex-col h-fit gap-y-f2">
    <span>Your information has been submitted for review.</span>
  </div>
);

type PersonCDDStatus = 'submitted' | 'incomplete';

const deriveStatus = (entityStatus: string): PersonCDDStatus =>
  entityStatus === LegalEntityStatusSchema.Enum['pending-submission']
    ? 'incomplete'
    : 'submitted';

const PersonCDDViewInner = ({ entityId }: PersonCDDViewProps) => {
  const workspaceId = useWorkspaceId();
  const { context: internalApiCtx } = useInternalApiContext();
  const { showToast } = useToast();
  const { mode, systemIsDark } = useContextSafely(ColorModeContext);
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemIsDark);

  // fetch entity
  const [{ data: entityData, error: entityError, fetching: isFetchingEntity }] =
    useGetIndividualEntityQuery({
      variables: { workspaceId, entityId },
      context: internalApiCtx,
    });

  const { result: entityResult, isError: isEntityError } = getResultOrError(
    entityData?.individualEntity,
    entityError
  );

  const needsPlatformForSubmit =
    entityResult?.individualEntity.status ===
    LegalEntityStatusSchema.Enum['pending-submission'];

  const [
    { data: platformData, error: platformError, fetching: isFetchingPlatform },
  ] = useGetPaymentPlatformEntityQuery({
    variables: { workspaceId },
    context: internalApiCtx,
    pause: !needsPlatformForSubmit,
  });

  const { result: platformResult, isError: isPlatformQueryError } =
    getResultOrError(platformData?.paymentPlatformEntity, platformError);

  const businessEntityId = useMemo(() => {
    if (!needsPlatformForSubmit || isPlatformQueryError) {
      return undefined;
    }
    return platformResult.platformEntity.id;
  }, [needsPlatformForSubmit, isPlatformQueryError, platformResult]);

  const piiDataTokenId = useMemo(
    () => entityResult?.individualEntity.piiDataTokenId,
    [entityResult]
  );

  // authorize basis theory session
  const { bt } = useBasisTheory();
  const [, authorizeSession] =
    useAuthorizeIndividualLegalEntityBasisTheorySessionMutation();

  const {
    data: sessionKey,
    error: sessionError,
    isFetching: isFetchingSession,
  } = useQuery({
    queryKey: ['bt-session-person', workspaceId, entityId],
    enabled: bt != null,
    retry: 3,
    queryFn: async () => {
      const { sessionKey: key, nonce } = await bt!.sessions.create();

      const authResult = await authorizeSession(
        { workspaceId, entityId, nonce },
        internalApiCtx
      );

      if (authResult.error) {
        throw new Error(authResult.error.message);
      }

      const data =
        authResult.data?.authorizeIndividualLegalEntityBasisTheorySession;
      if (!data) {
        throw new Error('No response from server');
      }

      if (
        data.__typename === 'BadRequestError' ||
        data.__typename === 'InternalError'
      ) {
        throw new Error(data.message);
      }

      if (!data.success) {
        throw new Error('Session authorization failed');
      }

      return key;
    },
  });

  // retrieve the PII token
  const {
    data: token,
    error: tokenError,
    isFetching: isFetchingToken,
  } = useQuery({
    queryKey: ['token', piiDataTokenId],
    enabled: bt != null && sessionKey != null && piiDataTokenId != null,
    retry: 3,
    queryFn: async () =>
      bt!.tokens.retrieve(piiDataTokenId!, {
        apiKey: sessionKey!,
      }),
  });

  const updateToken = useCallback(
    (id: string, ref: React.RefObject<ITextElement>) => {
      if (bt && piiDataTokenId && ref.current) {
        bt.tokens
          .update(piiDataTokenId, {
            data: {
              [id]: ref.current,
            },
          })
          .then(() => {})
          .catch(() => {
            showToast(<Toast type="error" message="Failed to save data." />);
          });
      }
    },
    [bt, piiDataTokenId, showToast]
  );

  const [statusOverride, setStatusOverride] = useState<PersonCDDStatus | null>(
    null
  );

  const entityStatus = entityResult?.individualEntity.status;
  const status =
    statusOverride ??
    (entityStatus ? deriveStatus(entityStatus) : 'incomplete');

  const statusView = useMemo(
    () =>
      match<PersonCDDStatus>(status)
        .with('submitted', () => <PersonCDDSubmittedView />)
        .with('incomplete', () => (
          <PersonCDDForm
            workspaceId={workspaceId}
            legalEntityId={entityId}
            businessEntityId={businessEntityId!}
            token={token!}
            updateToken={updateToken}
            isDarkMode={isDarkMode}
            onSubmittedForReview={() => setStatusOverride('submitted')}
          />
        ))
        .exhaustive(),
    [
      status,
      token,
      updateToken,
      isDarkMode,
      workspaceId,
      entityId,
      businessEntityId,
    ]
  );

  const platformResolutionFailed =
    needsPlatformForSubmit &&
    !isFetchingPlatform &&
    (isPlatformQueryError || businessEntityId === undefined);

  if (isEntityError || sessionError || tokenError || platformResolutionFailed) {
    return (
      <div className="flex flex-col h-fit gap-y-f2 p-f2 max-w-f56">
        <Logo />
        <span className="text-main">
          Unable to load onboarding data. Please check the link and try again.
        </span>
      </div>
    );
  }

  if (
    isFetchingEntity ||
    isFetchingSession ||
    isFetchingToken ||
    (needsPlatformForSubmit && isFetchingPlatform) ||
    (needsPlatformForSubmit && businessEntityId === undefined) ||
    !token
  ) {
    return <PersonCDDLoadingView />;
  }

  return (
    <div className="flex flex-col h-fit gap-y-f2 p-f2 max-w-f56">
      <Logo />
      {statusView}
    </div>
  );
};

export const PersonCDDView = ({ entityId }: PersonCDDViewProps) => {
  const { dashboardApiKey } = FragmentEnv.basisTheory!;
  const { bt } = useBasisTheory(dashboardApiKey);
  const [agreedToCIP, setAgreedToCIP] = useState(false);
  if (!dashboardApiKey) {
    return <div>Basis Theory is not configured for this environment.</div>;
  }

  if (!agreedToCIP) {
    return (
      <div className="flex flex-col h-fit gap-y-f2 p-f2 max-w-f56">
        <Logo />
        <NoticeView
          onboardingState={{}}
          setOnboardingField={() => {}}
          onNext={() => setAgreedToCIP(true)}
        />
      </div>
    );
  }

  if (!bt) {
    return <PersonCDDLoadingView />;
  }

  return (
    <BasisTheoryProvider bt={bt}>
      <Suspense fallback={<PersonCDDLoadingView />}>
        <PersonCDDViewInner entityId={entityId} />
      </Suspense>
    </BasisTheoryProvider>
  );
};
